/**
 * Excel-style column filtering over the rows that are ALREADY LOADED (P30).
 *
 * Ported from `reporting_app/web/src/hooks/useColumnFilters.ts`, with one
 * deliberate change: reporting_app keeps the filter state in `useState` and
 * persists nothing, because there a filter left over from a previous visit is
 * the failure mode the page was built to remove. DRA's rule (P22) is the
 * opposite end of the same principle — the URL IS the state, so a reviewer can
 * send "the dch tickets nobody has identified" to a colleague. So this hook is
 * CONTROLLED: it filters, it counts, and it decides which values a popover may
 * offer, but it owns no state. `useTicketFilters` holds it in the query string.
 *
 * =========================================================================
 * WHAT THESE FILTERS SEE, AND WHAT THEY DO NOT
 * =========================================================================
 * They see the loaded page. `GET /v1/tickets` reads `TOP 500`, and a filter
 * over a truncated list is a filter that lies unless it says so — which is why
 * P30 also made the endpoint report the total and the table print
 * "500 of N loaded" when the two differ. A column that HAS a server parameter
 * (status, company, language, owner, review-reason family, created) never comes
 * through here at all: it writes its parameter and the SQL does the work.
 */

import { useCallback, useMemo } from 'react';

export interface ColumnFilterState {
    /** Values the row's column must match, as strings. `null` = the checkbox list imposes no restriction. */
    selected: Set<string> | null;
    /** Numeric range — applies only where the column is numeric. `null`/`undefined` = unbounded. */
    min?: number | null;
    max?: number | null;
}

export interface FilterColumn<Row> {
    key: string;
    accessor: (row: Row) => string | number | null | undefined;
    numeric?: boolean;
}

export function isColumnFilterActive(state: ColumnFilterState | undefined): boolean {
    return !!state && (state.selected !== null || state.min != null || state.max != null);
}

/** One row's value for a column, as the string the filter and the URL speak. */
function valueOf<Row>(col: FilterColumn<Row>, row: Row): string {
    const raw = col.accessor(row);
    return raw === null || raw === undefined ? '' : String(raw);
}

export function useColumnFilters<Row>(
    rows: Row[],
    columns: FilterColumn<Row>[],
    filters: Record<string, ColumnFilterState>,
) {
    // A row passes column `exceptKey`'s OWN filter unconditionally, so this doubles as both the
    // "final filtered set" (exceptKey = null) and, per column, "what would the other filters
    // leave standing" — which is what the popover needs to offer the right checkbox list.
    const passesExcept = useCallback((row: Row, exceptKey: string | null) => {
        for (const col of columns) {
            if (col.key === exceptKey) continue;
            const f = filters[col.key];
            if (!f) continue;
            const raw = col.accessor(row);
            if (f.min != null || f.max != null) {
                const n = typeof raw === 'number' ? raw : null;
                if (n === null) return false;
                if (f.min != null && n < f.min) return false;
                if (f.max != null && n > f.max) return false;
            }
            if (f.selected) {
                const s = raw === null || raw === undefined ? '' : String(raw);
                if (!f.selected.has(s)) return false;
            }
        }
        return true;
    }, [columns, filters]);

    const filtered = useMemo(
        () => rows.filter(r => passesExcept(r, null)),
        [rows, passesExcept],
    );

    /**
     * Unique values for one column's popover WITH their row counts, drawn from rows that already
     * pass every OTHER column's filter — narrowing one column narrows what the next column's list
     * offers, same as Excel's AutoFilter.
     *
     * The count is the point of the addition over the port: "Needs review 30" tells a reviewer
     * what the click is worth before they make it.
     */
    const valueCountsFor = useCallback((key: string): Array<{ value: string; count: number }> => {
        const col = columns.find(c => c.key === key);
        if (!col) return [];
        const counts = new Map<string, number>();
        for (const r of rows) {
            if (!passesExcept(r, key)) continue;
            const v = valueOf(col, r);
            counts.set(v, (counts.get(v) ?? 0) + 1);
        }
        return [...counts.entries()]
            .map(([value, count]) => ({ value, count }))
            .sort((a, b) => a.value.localeCompare(b.value, undefined, { numeric: true }));
    }, [rows, columns, passesExcept]);

    const isFiltered = useCallback((key: string) => key in filters, [filters]);
    const activeCount = Object.keys(filters).length;

    return { filtered, isFiltered, activeCount, valueCountsFor };
}
